import { Injectable, inject } from '@angular/core';
import { catchError, Observable, of, switchMap, take } from 'rxjs';

import { ConfirmationDrawerService } from '@ps/core-overlay';
import { SURVEY_STATUS, SurveyStatus } from '@ps/survey-kernel';
import { SurveyHighCpiConfirmationInspector } from '@ps/survey-utils';

import { SurveyService } from '../survey/survey.service';

export interface SurveyHighCpiConfirmationOptions {
  readonly isProject?: boolean;
  readonly nextStatus?: SurveyStatus;
}

export interface SurveyHighCpiEnteredConfirmationOptions {
  readonly currencySymbol?: string;
}

@Injectable()
export class SurveyHighCpiConfirmationService {
  private readonly _confirmationDrawer = inject(ConfirmationDrawerService);
  private readonly _surveyService = inject(SurveyService);

  /**
   * Confirms high CPI using the survey high-cpi-details API (status change to Live).
   */
  public request(
    surveyId: number,
    options: SurveyHighCpiConfirmationOptions = {},
  ): Observable<boolean> {
    const nextStatus = options.nextStatus ?? SURVEY_STATUS.LIVE;

    if (nextStatus !== SURVEY_STATUS.LIVE) {
      return of(true);
    }

    return this._surveyService.getHighCpiDetails(surveyId).pipe(
      switchMap((highCpiDetails) =>
        this._confirmIfNeeded(
          new SurveyHighCpiConfirmationInspector({
            type: 'high-cpi-data',
            nextStatus,
            highCpiDetails,
            isProject: options.isProject,
          }),
          {
            surveyIds: [surveyId],
            cpi: highCpiDetails.pricingDetails.value,
          },
          { stacked: true },
        ),
      ),
      catchError(() => of(true)),
    );
  }

  /**
   * Confirms high CPI when the user enters a value greater than {@link MAX_CPI_THRESHOLD}.
   */
  public requestForEnteredCpi(
    surveyId: number,
    cpi: number | string,
    options: SurveyHighCpiEnteredConfirmationOptions = {},
  ): Observable<boolean> {
    return this._confirmIfNeeded(
      new SurveyHighCpiConfirmationInspector({
        type: 'survey-data',
        cpi: Number(cpi),
        currencySymbol: options.currencySymbol ?? '$',
      }),
      { surveyIds: [surveyId], cpi },
      { stacked: true },
    );
  }

  /**
   * Confirms high CPI for a bulk CPI edit. Opens the drawer once and logs
   * the confirmation for every survey in the selection.
   */
  public requestForEnteredBulkCpi(
    surveyIds: number[],
    cpi: number | string,
    options: SurveyHighCpiEnteredConfirmationOptions = {},
  ): Observable<boolean> {
    return this._confirmIfNeeded(
      new SurveyHighCpiConfirmationInspector({
        type: 'survey-data',
        cpi: Number(cpi),
        currencySymbol: options.currencySymbol ?? '$',
      }),
      { surveyIds, cpi },
    );
  }

  private _confirmIfNeeded(
    inspector: SurveyHighCpiConfirmationInspector,
    activity: { surveyIds: number[]; cpi: number | string },
    drawerOptions: { stacked?: boolean } = {},
  ): Observable<boolean> {
    if (!inspector.check()) {
      return of(true);
    }

    return new Observable<boolean>((observer) => {
      const ref = this._confirmationDrawer.openRef(
        {
          title: 'CPI Confirmation',
          subtitle: 'Please review the value before confirming',
          message: inspector.text(),
          confirmLabel: 'Confirm',
          cancelLabel: 'Cancel',
        },
        drawerOptions,
      );

      const sub = ref.closed$.pipe(take(1)).subscribe((confirmed) => {
        if (confirmed) {
          this._logConfirmations(activity.surveyIds, activity.cpi);
          observer.next(true);
        }
        observer.complete();
      });

      // Close the drawer if the parent flow is cancelled (e.g. status drawer
      // backdrop click) so the CPI drawer doesn't stay orphaned on screen.
      return () => {
        sub.unsubscribe();
        ref.close();
      };
    });
  }

  private _logConfirmations(surveyIds: number[], cpi: number | string): void {
    surveyIds.forEach((id) =>
      this._surveyService.logHighCpiConfirmation(id, cpi).subscribe(),
    );
  }
}
